import { Col, Row, Typography } from "antd"
import { useTitle } from "hooks/useTitle"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { getGuideTrip } from "store/trips"
import { buildPreviewPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"
import { PreviewGuide } from "pagesV2/pro/Experiences/ListingPreview/PreviewGuide"
import { PreviewMediaListing } from "pagesV2/pro/Experiences/ListingPreview/PreviewMedia"
import BookingDetailCard from "pagesV2/pro/Embedded/ExperienceBooking/BookingDetailCard"
import ActionsPanel from "pagesV2/pro/Experiences/ListingPreview/ActionsPanel"
import { PreviewCategories } from "../components/preview/PreviewCategories/PreviewCategories"
import { PreviewDetails } from "../components/preview/PreviewDetails/PreviewDetails"
import { PreviewScheduling } from "../components/preview/PreviewScheduling/PreviewScheduling"
import { PreviewTicketTypes } from "../components/preview/PreviewTicketTypes/PreviewTicketTypes"
import { PreviewLocation } from "../components/preview/PreviewLocation/PreviewLocation"
import { PreviewPricing } from "../components/preview/PreviewPricing/PreviewPricing"
import { PreviewAddOns } from "../components/preview/PreviewAddOns/PreviewAddOns"
import styles from "pagesV2/pro/Experiences/ListingPreview/ExperiencesPreview.module.css"
import { routes } from "router"

const { Title } = Typography

/**
 * Preview step — shows a live preview of the experience built from the shared
 * antd form values and the mediaFilesRef, merged on top of the server trip.
 *
 * Server trip is still fetched (for guide info, lat/lng) but all wizard-edited
 * fields are read from the form by each preview section component.
 *
 * Layout mirrors the wizard tabs:
 *   Details      → PreviewDetails (description, items, languages, skills, capacity)
 *   Scheduling   → PreviewScheduling (timezone, season, days, hours)
 *   Ticket Type  → PreviewTicketTypes (duration, capacity, types)
 *   Pricing      → PreviewPricing (type, deposit, adjustments, fees)
 *   Location     → PreviewLocation (address, map, directions)
 *   Media        → PreviewMediaListing (photos, videos)
 *
 * When accessed via ExperienceCard preview button (readOnlyPreview=true),
 * shows ActionsPanel instead of BookingDetailCard.
 */
export const PreviewStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { form, mediaFilesRef, stepSaveHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))

  const isPreviewMode = searchParams.get("readOnlyPreview") === "true"
  const routeType = searchParams.get("route")

  useLoaderWatchers({ watchers: ["trips.trip"] })

  useEffect(() => {
    if (id) dispatch(getGuideTrip(id, true, true))
  }, [id, dispatch])

  // Register save handler — marks wizard as complete when user publishes
  useEffect(() => {
    stepSaveHandlersRef.current["preview"] = async () => {
      return buildPreviewPayload()
    }
    return () => {
      delete stepSaveHandlersRef.current["preview"]
    }
  }, [stepSaveHandlersRef])

  // previewTrip is used only by components that still depend on server fields:
  // PreviewMediaListing (photos/videos from mediaFilesRef), PreviewGuide (guide info).
  const previewTrip = useMemo(() => {
    const activeMedia = (mediaFilesRef.current || []).filter((m) => !m._destroy)
    return {
      ...trip,
      photos: activeMedia.filter((m) => m.file_type === "photo"),
      cropped_photos: activeMedia.filter((m) => m.file_type === "cropped_photo"),
      videos: activeMedia.filter((m) => m.file_type === "video"),
      trimmed_videos: activeMedia.filter((m) => m.file_type === "trimmed_video")
    }
  }, [trip, mediaFilesRef])

  const title = form.getFieldValue("title") || trip?.title
  useTitle(title)

  const handleEdit = () => {
    navigate(routes.proEditExperienceV2Path({ id: trip?.id }))
  }

  return (
    <>
      <Title level={3}>
        {title}
      </Title>
      <PreviewCategories />
      <PreviewMediaListing trip={previewTrip} />

      <Row gutter={20}>
        <Col span={24} lg={24} xl={15}>
          <PreviewDetails />
          <PreviewScheduling />
          <PreviewTicketTypes />
          <PreviewLocation />
          <PreviewAddOns />
          <PreviewGuide trip={previewTrip} t={t} />
        </Col>
        <Col span={24} lg={24} xl={9} className={styles.formCard}>
          {isPreviewMode ? (
            <ActionsPanel trip={previewTrip} onEdit={handleEdit} routeType={routeType} />
          ) : (
            <BookingDetailCard bookNow={false} trip={previewTrip} bookingButtonVisible />
          )}
          <PreviewPricing />
        </Col>
      </Row>
    </>
  )
}
